import { useEffect, useMemo, useRef, useState } from 'react';
import { X, ArrowRightLeft, Check, AlertTriangle } from 'lucide-react';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  return fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'include',
  });
}
const uur = (n) => (Math.round(n * 100) / 100).toLocaleString('nl-NL', { maximumFractionDigits: 2 });

// Voorbeeld + bevestigen: Damen-uren van één week in de urenlijst van dit bedrijf zetten.
export default function DamenNaarUrenlijst({ profiel, profielNaam, jaar, week, onSluit }) {
  const [v, setV] = useState(null);
  const [fout, setFout] = useState('');
  const [keuze, setKeuze] = useState({}); // sfId → workerId ('' = niet)
  const [mee, setMee] = useState({});     // sfId → true/false
  const [bezig, setBezig] = useState(false);
  const [klaar, setKlaar] = useState(null);

  useEffect(() => {
    authFetch(`/api/damen-uren/koppel?profiel=${profiel}&jaar=${jaar}&week=${week}`).then(async (r) => {
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Laden mislukt');
      setV(d);
      setKeuze(Object.fromEntries(d.rijen.map((x) => [x.sfId, x.match ? String(x.match.workerId) : ''])));
      // Standaard mee: alles met een medewerker, behalve wat al precies zo in de urenlijst staat
      setMee(Object.fromEntries(d.rijen.map((x) => [x.sfId, !!x.match && !x.gelijk])));
    }).catch((e) => setFout(e.message));
  }, [profiel, jaar, week]);

  const dubbel = useMemo(() => {
    const tel = {};
    for (const [id, w] of Object.entries(keuze)) if (w && mee[id]) tel[w] = (tel[w] || 0) + 1;
    return new Set(Object.keys(tel).filter((w) => tel[w] > 1));
  }, [keuze, mee]);
  const gekozen = v ? v.rijen.filter((x) => mee[x.sfId] && keuze[x.sfId]) : [];
  // "Alles selecteren": alleen rijen met een medewerker kunnen mee
  const kunnenMee = v ? v.rijen.filter((x) => keuze[x.sfId]) : [];
  const allesAan = kunnenMee.length > 0 && kunnenMee.every((x) => mee[x.sfId]);
  const deelsAan = !allesAan && kunnenMee.some((x) => mee[x.sfId]);
  const allesRef = useRef(null);
  useEffect(() => { if (allesRef.current) allesRef.current.indeterminate = deelsAan; }, [deelsAan]);
  const zetAlles = (aan) => setMee((m) => ({ ...m, ...Object.fromEntries(kunnenMee.map((x) => [x.sfId, aan])) }));

  const uitvoeren = async () => {
    setBezig(true); setFout('');
    try {
      const r = await authFetch('/api/damen-uren/koppel', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profiel, jaar, week, toewijzingen: gekozen.map((x) => ({ sfId: x.sfId, workerId: Number(keuze[x.sfId]) })) }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Overzetten mislukt');
      setKlaar(d);
    } catch (e) {
      setFout(e.message);
    } finally {
      setBezig(false);
    }
  };

  return (
    <div className="du-modal" role="dialog" aria-modal="true" aria-labelledby="du-nu-titel" onClick={(e) => e.target === e.currentTarget && onSluit()}>
      <div className="du-modal-in">
        <button type="button" className="du-sluit" onClick={onSluit} aria-label="Sluiten"><X size={18} /></button>
        <h3 id="du-nu-titel"><ArrowRightLeft size={18} /> Week {week} naar urenlijst {profielNaam}</h3>

        {klaar ? (
          <div className="du-klaar">
            <p><Check size={18} /> {klaar.aantal} {klaar.aantal === 1 ? 'persoon' : 'personen'} in de urenlijst gezet
              {klaar.nieuweLijst ? ' (nieuwe lijst aangemaakt)' : ''}. Kijk het na onder <strong>Urenlijsten</strong> → {profielNaam} → week {week}.</p>
            <div className="du-paneel-acties"><button type="button" className="du-btn du-btn-primary" onClick={onSluit}>Sluiten</button></div>
          </div>
        ) : !v ? <p className="du-empty">{fout || 'Laden…'}</p> : (
          <>
            <p className="du-modal-sub">
              Klant in de urenlijst: <strong>{v.klant?.naam || 'niet gevonden'}</strong>.{' '}
              {v.lijst ? 'De lijst van deze week bestaat al; aangevinkte personen worden overschreven met de Damen-uren.' : 'Er is nog geen lijst voor deze week; die wordt aangemaakt.'}
            </p>
            {v.lijst?.locked && <p className="du-waarschuwing"><AlertTriangle size={16} /> Deze week is vergrendeld in de urenlijst. Ontgrendel hem eerst.</p>}
            {!v.klant && <p className="du-waarschuwing"><AlertTriangle size={16} /> Klant "Damen Schiedam" ontbreekt bij Klanten.</p>}

            <div className="du-scroll">
              <table className="du-table du-koppel">
                <thead>
                  <tr><th className="du-name">Damen</th><th className="du-name">Medewerker bij ons</th><th>Damen</th><th>Nu in lijst</th>
                    <th className="du-alles">
                      <label>
                        <input ref={allesRef} type="checkbox" checked={allesAan} disabled={!kunnenMee.length}
                          onChange={(e) => zetAlles(e.target.checked)} aria-label="Alles selecteren" />
                        <span>Alles</span>
                      </label>
                    </th></tr>
                </thead>
                <tbody>
                  {v.rijen.map((x) => {
                    const w = keuze[x.sfId];
                    const status = !w ? 'geen' : x.match && String(x.match.workerId) === w ? (x.match.zeker ? 'zeker' : 'check') : 'hand';
                    return (
                      <tr key={x.sfId} className={dubbel.has(w) ? 'is-dubbel' : ''}>
                        <td className="du-name"><strong>{x.damenNaam}</strong><small>{x.ts}</small>
                          {x.locaties && <small className="du-locaties">{x.locaties.length} locaties samengeteld: {x.locaties.map((l) => `${l.klant.replace(/^Damen Shiprepair /, '').replace(/ B\.?V\.?$/, '')} ${uur(l.uren)}`).join(' + ')}</small>}
                        </td>
                        <td className="du-name">
                          <select value={w} onChange={(e) => { setKeuze({ ...keuze, [x.sfId]: e.target.value }); setMee({ ...mee, [x.sfId]: !!e.target.value }); }}
                            aria-label={`Medewerker voor ${x.damenNaam}`}>
                            <option value="">— niet overzetten —</option>
                            {v.medewerkers.map((m) => <option key={m.id} value={m.id}>{m.naam}</option>)}
                          </select>
                          <span className={`du-match du-match--${status}`}>
                            {status === 'zeker' ? (x.match.onthouden ? 'onthouden' : 'naam klopt') : status === 'check' ? 'controleer' : status === 'hand' ? 'handmatig' : 'niet gevonden'}
                          </span>
                        </td>
                        <td><strong>{uur(x.totaal)}</strong>{x.buitenWeek > 0 && <small className="du-rood">{uur(x.buitenWeek)} buiten week</small>}</td>
                        <td>{x.bestaandTotaal == null ? '–' : x.gelijk ? <span className="du-gelijk">gelijk</span> : uur(x.bestaandTotaal)}</td>
                        <td><input type="checkbox" checked={!!mee[x.sfId]} disabled={!w} onChange={(e) => setMee({ ...mee, [x.sfId]: e.target.checked })} aria-label={`${x.damenNaam} overzetten`} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {dubbel.size > 0 && <p className="du-info">Eén medewerker is aan meerdere Damen-namen gekoppeld (gemarkeerd). Hun uren worden bij elkaar opgeteld.</p>}
            {fout && <p className="du-waarschuwing">{fout}</p>}
            <div className="du-paneel-acties">
              <span className="du-modal-sub">Gekoppelde namen worden onthouden voor de volgende keer.</span>
              <button type="button" className="du-btn" onClick={onSluit}>Annuleren</button>
              <button type="button" className="du-btn du-btn-primary" onClick={uitvoeren}
                disabled={bezig || !gekozen.length || v.lijst?.locked || !v.klant}>
                {bezig ? 'Bezig…' : `${gekozen.length} ${gekozen.length === 1 ? 'persoon' : 'personen'} overzetten`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
